import {ChangeEvent, Fragment, useState} from 'react';
import styled from 'styled-components';
import {useUpdateProfile} from '@/api/mypage';
import {getGqlErrorMessage} from '@/api/request';
import {USER_EXTRA_INFO_STEPS, USER_PURPOSE_OPTIONS} from '@/constants';
import {ProductType, UserCategoryType, UserProfileType, UserPurposeType} from '@/enums';
import {useSession} from '@/hooks';
import {useAirbridgeTracking, useGA4WithParams} from '@/hooks/tracking';
import {
  Divider, FlexCenter, FlexFullGrow, PaddingBox, StepDot, StepDots
} from '@/styled-components';
import {palette} from '@/styles';
import {setAirbridgeOnboardingProfile} from '@/utils';
import {Button, ButtonGroup} from '../Button';
import {Checkbox, CheckboxItem, CheckboxLabel} from '../Checkbox';
import toast from '../HotToast';
import {Modal} from '../Modal';
import {TextField} from '../TextField';
import {Typography} from '../Typography';

const STEPS: Array<{
  id: UserProfileType;
  label: string;
  isMulti?: boolean;
  options: Array<{
    id: string;
    label: string;
    hint?: string;
  }>;
}> = [
  ...USER_EXTRA_INFO_STEPS,
  {
    id: UserProfileType.purpose,
    label: '마이프차 지도를 사용하는 목적을 골라주세요.',
    isMulti: true,
    options: USER_PURPOSE_OPTIONS,
  },
];

interface IProps {
  onComplete(): void;
}

const MIN_OTHER_COUNT = 2;
const MAX_OTHER_COUNT = 500;

export const Onboarding = ({onComplete}: IProps) => {
  const [values, setValues] = useState<{[key: string]: any}>({});
  const [step, setStep] = useState<number>(0);
  const tracking = useGA4WithParams();
  const trackAirbridge = useAirbridgeTracking();
  const {user} = useSession();

  const {mutate} = useUpdateProfile();

  const onClick = () => {
    if (step + 1 === STEPS.length) {
      onSubmit();
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const onSubmit = () => {
    mutate(values, {
      onSuccess: (data) => {
        const profile = data.result?.profile;

        tracking('onboarding_complete', profile);
        setAirbridgeOnboardingProfile(profile);

        const productType =
          user?.billing?.productType === ProductType.Subscription
            ? '구독'
            : '구매';

        trackAirbridge('complete_onboarding', {
          action: profile?.[UserProfileType.gender],
          label: profile?.[UserProfileType.ageGroup],
          semanticAttributes: {
            category: profile?.[UserProfileType.category],
            categoryOther: profile?.[UserProfileType.otherCategory],
          },
          customAttributes: {
            productType,
            purpose: profile?.[UserProfileType.purpose],
            purposeOther: profile?.[UserProfileType.other],
          },
        });

        onComplete();
      },
      onError: (error) => {
        toast.error(getGqlErrorMessage(error));
      },
    });
  };

  const onTextFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {checked, name} = e.target;
    const {type, multi} = e.target.dataset;

    if (type) {
      setValues((prev) => {
        if (checked) {
          if (
            type === UserProfileType.category &&
            name !== UserCategoryType.Other
          ) {
            prev[UserProfileType.otherCategory] = undefined;
          }

          if (multi === 'multi') {
            if (!prev[type]) {
              prev[type] = [];
            }
            prev[type].push(name);
          } else {
            prev[type] = name;
          }
        } else {
          if (
            type === UserProfileType.purpose &&
            name === UserPurposeType.Other
          ) {
            prev[UserProfileType.other] = undefined;
          } else if (
            type === UserProfileType.category &&
            name === UserCategoryType.Other
          ) {
            prev[UserProfileType.otherCategory] = undefined;
          }

          if (multi === 'multi') {
            prev[type] = prev[type].filter((x: any) => x !== name);
          } else {
            prev[type] = undefined;
          }
        }

        return {...prev};
      });
    }
  };

  const curStep = STEPS[step];
  const curValue = values[curStep.id];

  const showEtcForm =
    (curStep.id === UserProfileType.category &&
      curValue === UserCategoryType.Other) ||
    (curStep.id === UserProfileType.purpose &&
      curValue?.includes(UserPurposeType.Other));
  const textfieldName =
    curStep.id === UserProfileType.category
      ? UserProfileType.otherCategory
      : UserProfileType.other;

  return (
    <Modal width={328}>
      <Root>
        <Box>
          <Typography variant="bold18" align="center">
            환영합니다!
          </Typography>
          <Typography variant="medium14" color={palette.gray600}>
            회원님에게 필요한 데이터를 제공할 수 있도록 회원님에 대해 알려주세요
            ☺️
          </Typography>
          <FlexCenter>
            <StepDots>
              {STEPS.map((i) => (
                <StepDot key={i.id} active={curStep.id === i.id} />
              ))}
            </StepDots>
          </FlexCenter>
        </Box>
        <Box>
          <Typography variant="medium14">
            {curStep.label}
            <Typography color={palette.primary400}> *</Typography>
            {curStep.isMulti && (
              <>
                <br />
                (중복 선택 가능)
              </>
            )}
          </Typography>
          <Box>
            {curStep.options.map((i, idx) => (
              <Fragment key={idx}>
                {idx > 0 && <Divider />}
                <CheckboxItem key={i.id} gap="8px">
                  <Checkbox
                    checked={
                      curStep.isMulti
                        ? curValue?.includes(i.id)
                        : curValue === i.id
                    }
                    data-type={curStep.id}
                    data-multi={curStep.isMulti ? 'multi' : undefined}
                    name={i.id}
                    variant="large"
                    onChange={onChange}
                  />
                  <CheckboxLabel>
                    {i.label}
                    {!!i.hint?.length && (
                      <Typography variant="medium12" color={palette.gray600}>
                        {' '}
                        ({i.hint})
                      </Typography>
                    )}
                  </CheckboxLabel>
                </CheckboxItem>
              </Fragment>
            ))}
            {showEtcForm && (
              <div>
                {showEtcForm && (
                  <TextField
                    variant="large"
                    fullWidth
                    placeholder={
                      curStep.id === UserProfileType.purpose
                        ? '기타 서비스 사용 목적을 알려주세요.'
                        : '기타에 해당하시는 경우 자세히 알려주세요.'
                    }
                    maxLength={MAX_OTHER_COUNT}
                    name={textfieldName}
                    value={values[textfieldName]}
                    onChange={onTextFieldChange}
                  />
                )}
                <PaddingBox top={8}>
                  <Typography
                    as="div"
                    align="right"
                    variant="paragraph12"
                    lineHeight="20px"
                    color={palette.gray600}
                  >
                    최소 {MIN_OTHER_COUNT}자~ 최대 {MAX_OTHER_COUNT}자
                  </Typography>
                </PaddingBox>
              </div>
            )}
          </Box>
        </Box>
        <FlexFullGrow />
        <ButtonGroup spacing={2} flex={1}>
          {step > 0 && (
            <Button
              size="medium"
              color="white"
              variant="fill"
              width={96}
              onClick={() => setStep((prev) => prev - 1)}
            >
              이전
            </Button>
          )}
          <Button
            size="medium"
            color="secondary"
            variant="fill"
            fullWidth
            disabled={
              !curValue?.length ||
              (showEtcForm &&
                (!values[textfieldName] ||
                  values[textfieldName].length < MIN_OTHER_COUNT))
            }
            onClick={onClick}
          >
            {step + 1 === STEPS.length ? '완료' : '다음'}
          </Button>
        </ButtonGroup>
      </Root>
    </Modal>
  );
};

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Root = styled(Box)`
  min-height: 518px;
  padding: 24px;
`;
